const BaseElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;
let inlineRegistration;

export function productUrl(value, baseUrl, previewThemeId) {
  try {
    const base = new URL(baseUrl);
    const url = new URL(value, base);
    if (url.origin !== base.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) return null;
    url.hash = '';
    if (previewThemeId && /^\d+$/.test(String(previewThemeId))) url.searchParams.set('preview_theme_id', String(previewThemeId));
    return url;
  } catch { return null; }
}

export function resultProductUrl(product, size, selectedUrl, baseUrl, previewThemeId, normalizeSize) {
  const url = productUrl(selectedUrl, baseUrl, previewThemeId);
  if (!url) return null;
  url.searchParams.delete('variant');
  const index = product?.options?.findIndex((name) => /^(?:size|gr[oö]sse|größe)$/i.test(name));
  if (!size || !Number.isInteger(index) || index < 0 || typeof normalizeSize !== 'function') return url.href;
  const variants = (product.variants || []).filter((variant) => normalizeSize(variant.options?.[index]) === normalizeSize(size));
  const id = variants[0]?.id;
  if ((typeof id === 'string' && /^\d+$/.test(id)) || (typeof id === 'number' && Number.isSafeInteger(id) && id > 0)) url.searchParams.set('variant', String(id));
  return url.href;
}

export function captureProfile(finder) {
  if (!finder?.state) return null;
  return {
    state: { ...finder.state }, heightUnit: finder.heightUnit, weightUnit: finder.weightUnit,
    remember: !!finder.remember, step: Math.max(1, Math.min(4, Number(finder.step) || 1)),
    imperialDraft: finder.imperialDraft ? { ...finder.imperialDraft } : null,
  };
}

function inlineClass(module) {
  return class ZehnFitguideFinder extends module.ZehnSizeFinder {
    connectedCallback() {
      this.ready = this.initializeInline();
      return this.ready;
    }

    async initializeInline() {
      await super.connectedCallback();
      if (!this.events || !this.isConnected) return;
      this.dialog.open = true;
      this.dialog.setAttribute('role', 'region');
      this.dialog.removeAttribute('aria-modal');
      const snapshot = this.profileSnapshot;
      if (snapshot) {
        this.state = { ...snapshot.state };
        this.heightUnit = snapshot.heightUnit === 'imperial' ? 'imperial' : 'cm';
        this.weightUnit = snapshot.weightUnit === 'lb' ? 'lb' : 'kg';
        this.remember = snapshot.remember;
        this.imperialDraft = snapshot.imperialDraft ? { ...snapshot.imperialDraft } : null;
        this.syncControls();
        this.updateEstimate();
        this.selectedSize = this.estimate?.size || null;
        if (snapshot.step === 4) this.renderResult();
        this.setStep(snapshot.step, false);
      }
      this.updateAvailability();
    }

    captureProfile() { return captureProfile(this); }
    revealOpeners() {}
    onExternalClick() {}
    onVariantChange() {}
    syncColors() {}
    selectColor() { return false; }
    open() {}
    close() { this.operation = (this.operation || 0) + 1; }
    afterClose() { this.operation = (this.operation || 0) + 1; }

    setMode(mode) {
      if (mode === 'chart') { this.fitguideHost?.setTab('charts', true); return; }
      super.setMode('finder');
    }

    setStep(step, focus = true) {
      super.setStep(step, focus);
      if (focus && this.isConnected) this.querySelector(`[data-zf-step="${step}"]`)?.scrollIntoView({ block: 'start', behavior: 'auto' });
    }

    updateAvailability() {
      for (const selector of ['[data-zf-add]', '[data-zf-select]', '[data-zf-availability]']) {
        const element = this.querySelector(selector);
        if (element) { element.hidden = true; if ('disabled' in element) element.disabled = true; }
      }
      const link = this.querySelector('[data-zfg-product-link]');
      if (!link) return;
      const host = this.fitguideHost;
      link.hidden = this.step !== 4;
      link.textContent = this.result ? host?.dataset.productLinkText || '' : host?.dataset.productFallbackText || '';
      const href = resultProductUrl(this.productData, this.result?.size, this.selectedProductUrl, document.baseURI, host?.dataset.previewThemeId, module.normalizeSize);
      if (href) link.href = href;
      else { link.hidden = true; link.removeAttribute('href'); }
    }
  };
}

async function registerInline(moduleUrl) {
  if (!inlineRegistration) inlineRegistration = import(moduleUrl).then((module) => {
    if (typeof module.ZehnSizeFinder !== 'function' || typeof module.parseChart !== 'function') throw new Error('Finder module unavailable');
    if (!customElements.get('zehn-fitguide-finder')) customElements.define('zehn-fitguide-finder', inlineClass(module));
    return module;
  }).catch((error) => { inlineRegistration = null; throw error; });
  return inlineRegistration;
}

export class ZehnFitguide extends BaseElement {
  connectedCallback() {
    if (this.events) return;
    this.events = new AbortController();
    this.requestNumber = 0;
    this.groupGuidance();
    const options = { signal: this.events.signal };
    this.addEventListener('click', (event) => {
      const tab = event.target.closest?.('[data-zfg-tab]');
      if (tab && this.contains(tab)) { event.preventDefault(); this.setTab(tab.dataset.zfgTab); }
      const retry = event.target.closest?.('[data-zfg-retry]');
      if (retry && this.contains(retry)) { event.preventDefault(); this.loadProduct(); }
    }, options);
    this.addEventListener('keydown', (event) => this.onTabKeydown(event), options);
    this.querySelector('[data-zfg-product]')?.addEventListener('change', () => this.loadProduct(), options);
    this.querySelector('[data-zfg-category]')?.addEventListener('change', () => this.filterCharts(), options);
    this.setTab('finder');
    const tablist = this.querySelector('[data-zfg-tablist], [role="tablist"]');
    if (tablist) tablist.hidden = false;
    this.dataset.ready = 'true';
    this.classList.add('is-ready');
    this.showStatus('empty');
    if (this.querySelector('[data-zfg-product]')?.value) this.loadProduct();
  }

  disconnectedCallback() {
    this.requestNumber = (this.requestNumber || 0) + 1;
    clearTimeout(this.deadline);
    this.deadline = null;
    this.request?.abort();
    this.events?.abort();
    this.events = null;
  }

  groupGuidance() {
    const original = this.querySelector('[data-zfg-original]');
    if (!original) return;
    const category = this.querySelector('[data-zfg-category]');
    let chartNumber = this.querySelectorAll('[data-zfg-category-group]').length;
    for (const child of Array.from(original.children)) {
      if (child.tagName === 'SCRIPT' || (!child.textContent.trim() && !child.querySelector('img, video, table, details'))) continue;
      const group = child.matches('size-chart-group') ? 'charts'
        : child.matches('details.service-panel, service-collapsible, .service-accordion') ? 'measure' : 'tips';
      const destination = this.querySelector(`[data-zfg-content="${group}"]`) || this.querySelector(`[data-zfg-panel="${group}"]`);
      if (!destination) continue;
      if (group === 'charts') {
        child.dataset.zfgCategory = String(chartNumber++);
        child.setAttribute('data-zfg-category-group', '');
        if (category) {
          const option = document.createElement('option');
          option.value = child.dataset.zfgCategory;
          option.textContent = child.querySelector('.sgc__title, h3')?.textContent.trim() || option.value;
          category.append(option);
        }
      }
      destination.append(child);
    }
    original.hidden = true;
    this.filterCharts();
  }

  filterCharts() {
    const value = this.querySelector('[data-zfg-category]')?.value || '';
    for (const group of this.querySelectorAll('[data-zfg-category-group]')) group.hidden = value !== '' && group.dataset.zfgCategory !== value;
  }

  setTab(value, focus = false) {
    const tabs = Array.from(this.querySelectorAll('[data-zfg-tab]'));
    const selected = tabs.find((tab) => tab.dataset.zfgTab === value) || tabs[0];
    if (!selected) return;
    this.activeTab = selected.dataset.zfgTab;
    for (const tab of tabs) {
      const active = tab === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    }
    for (const panel of this.querySelectorAll('[data-zfg-panel]')) panel.hidden = panel.dataset.zfgPanel !== this.activeTab;
    if (focus) selected.focus({ preventScroll: true });
    const tablist = selected.closest?.('[data-zfg-tablist], [role="tablist"]');
    if (tablist?.getBoundingClientRect && tablist.scrollWidth > tablist.clientWidth) {
      const track = tablist.getBoundingClientRect();
      const button = selected.getBoundingClientRect();
      if (button.left < track.left) tablist.scrollLeft -= track.left - button.left;
      else if (button.right > track.right) tablist.scrollLeft += button.right - track.right;
    }
  }

  onTabKeydown(event) {
    const tab = event.target.closest?.('[data-zfg-tab]');
    if (!tab || !this.contains(tab)) return;
    const tabs = Array.from(this.querySelectorAll('[data-zfg-tab]'));
    const index = tabs.indexOf(tab);
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    this.setTab(tabs[next].dataset.zfgTab, true);
  }

  showStatus(kind) {
    const status = this.querySelector('[data-zfg-status]');
    const key = kind === 'loading' ? 'loadingText' : kind === 'error' ? 'errorText' : 'emptyText';
    if (status) { status.textContent = kind === 'ready' ? '' : this.dataset[key] || ''; status.hidden = kind === 'ready'; }
    const retry = this.querySelector('[data-zfg-retry]');
    if (retry) retry.hidden = kind !== 'error';
    const mount = this.querySelector('[data-zfg-mount]');
    if (mount) mount.setAttribute('aria-busy', String(kind === 'loading'));
    const fallback = this.querySelector('[data-zfg-fallback]');
    if (fallback) fallback.hidden = kind !== 'error' || !fallback.getAttribute('href');
  }

  async loadProduct() {
    const selection = this.querySelector('[data-zfg-product]');
    const mount = this.querySelector('[data-zfg-mount]');
    if (!selection || !mount) return;
    this.profileSnapshot = this.finder?.captureProfile?.() || this.profileSnapshot || null;
    this.finder = null;
    clearTimeout(this.deadline);
    this.deadline = null;
    this.request?.abort();
    const request = this.request = new AbortController();
    const number = this.requestNumber = (this.requestNumber || 0) + 1;
    const current = () => this.isConnected && this.requestNumber === number && !request.signal.aborted;
    mount.replaceChildren();
    const fallback = this.querySelector('[data-zfg-fallback]');
    fallback?.removeAttribute('href');
    if (!selection.value) { this.showStatus('empty'); return; }
    const url = productUrl(selection.value, document.baseURI, this.dataset.previewThemeId);
    if (!url) { this.showStatus('error'); return; }
    if (fallback) fallback.href = url.href;
    this.showStatus('loading');
    const deadline = this.deadline = setTimeout(() => {
      if (!current()) return;
      this.finder = null;
      mount.replaceChildren();
      this.showStatus('error');
      request.abort();
    }, 20000);
    try {
      const [response, module] = await Promise.all([
        fetch(url.href, { credentials: 'same-origin', signal: request.signal }),
        registerInline(this.dataset.finderModule),
      ]);
      if (!current()) return;
      if (!response.ok || !productUrl(response.url || url.href, document.baseURI, this.dataset.previewThemeId)) throw new Error('Product unavailable');
      const html = await response.text();
      if (!current()) return;
      const page = new DOMParser().parseFromString(html, 'text/html');
      const responseProductUrl = productUrl(response.url || url.href, document.baseURI, this.dataset.previewThemeId);
      const sources = Array.from(page.querySelectorAll('zehn-size-finder')).filter((source) => {
        let product;
        try { product = JSON.parse(source.querySelector('[data-zf-product]')?.textContent || 'null'); } catch { return false; }
        const sourceUrl = productUrl(product?.url, document.baseURI, this.dataset.previewThemeId);
        if (!sourceUrl || sourceUrl.pathname !== responseProductUrl.pathname || String(product.id) !== source.dataset.productId) return false;
        const tables = Array.from(source.querySelector('[data-zf-chart]')?.content?.querySelectorAll('.sg-table') || []);
        return tables.filter((table) => module.parseChart(table)).length === 1;
      });
      const source = sources[0];
      if (!source) throw new Error('Product chart unavailable');
      const finder = document.createElement('zehn-fitguide-finder');
      for (const attribute of source.attributes) finder.setAttribute(attribute.name, attribute.value);
      finder.innerHTML = source.innerHTML;
      finder.querySelectorAll('script:not([type="application/json"]), .zf-header, [data-zf-chart-view], [data-zf-color-panel], [data-zf-color-toggle], [data-zf-selected-color]').forEach((node) => node.remove());
      const dialog = finder.querySelector('dialog');
      if (!dialog) throw new Error('Product finder unavailable');
      dialog.setAttribute('open', '');
      dialog.removeAttribute('aria-modal');
      dialog.removeAttribute('aria-labelledby');
      dialog.setAttribute('role', 'region');
      dialog.setAttribute('aria-label', selection.selectedOptions?.[0]?.textContent || selection.value);
      const innerPanel = finder.querySelector('[data-zf-finder]');
      innerPanel?.removeAttribute('role');
      innerPanel?.removeAttribute('aria-labelledby');
      const link = document.createElement('a');
      link.className = 'zf-primary zfg-product-link';
      link.dataset.zfgProductLink = '';
      link.hidden = true;
      finder.querySelector('.zf-actions')?.append(link);
      finder.fitguideHost = this;
      finder.selectedProductUrl = url.href;
      finder.profileSnapshot = this.profileSnapshot;
      if (!current()) return;
      this.finder = finder;
      mount.replaceChildren(finder);
      await finder.ready;
      if (!current()) return;
      if (!finder.events || !finder.chart || !finder.engine) throw new Error('Product finder could not initialize');
      this.showStatus('ready');
    } catch (error) {
      if (!current()) return;
      this.finder = null;
      mount.replaceChildren();
      this.showStatus('error');
    } finally {
      clearTimeout(deadline);
      if (this.deadline === deadline) this.deadline = null;
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('zehn-fitguide')) customElements.define('zehn-fitguide', ZehnFitguide);
